<script lang="ts" setup>
import {
  useDialog,
  computed,
  useRouter,
  watch,
  nextTick,
  ref,
  waitMs,
  useTemplateRef,
} from "#imports";
import { onKeyStroke } from "@vueuse/core";
import MPButton from "../forms/MPButton.vue";

withDefaults(
  defineProps<{
    hasBackground?: boolean;
    bgColor?: string;
  }>(),
  {
    hasBackground: false,
    bgColor: "white",
  },
);

const {
  isOpen,
  currentComp,
  currentCompProps,
  closeDialog,
  isFullscreen,
  blockClose,
  closeBtnColor,
  registerComponent,
} = useDialog();

const dialog = useTemplateRef("dialog");
const componentRef = useTemplateRef("dialogComponentRef");

watch(componentRef, () => {
  registerComponent(componentRef.value);
});

watch(isOpen, async (newValue) => {
  if (newValue) {
    await nextTick();
    if (dialog.value) {
      dialog.value.showModal();
    }
  } else {
    if (dialog.value) {
      setTimeout(() => {
        dialog.value?.close();
      }, 300);
    }
  }
});

// Close dialog if route changes
const router = useRouter();
watch(router.currentRoute, () => {
  if (isOpen.value) {
    closeDialog(false);
  }
});

// Only close if the dialog was closed by native means (ESC key, click outside)
// and not by our closeDialog function
const onDialogClose = () => {
  if (isOpen.value) {
    closeDialog(true);
  }
};

// close when your clicks outside of the dialog element
const onDialogClick = (event: Event) => {
  const target = event.target as HTMLDialogElement;
  if (!blockClose.value && target === dialog.value) {
    // Clicked on backdrop
    closeDialog(true);
  }
};

onKeyStroke("Escape", (e) => {
  e.preventDefault();
  if (!blockClose.value) {
    onDialogClose();
  }
});

const closeButtonColor = computed(() => {
  return closeBtnColor.value === undefined ? "black" : closeBtnColor.value;
});

const isAnimating = ref(false);
const handleDrag = async () => {
  if (isAnimating.value) return;
  isAnimating.value = true;
  await waitMs(300);
  isAnimating.value = false;
};
</script>

<template>
  <Transition name="dialog-animation">
    <dialog
      v-if="isOpen"
      v-drag="handleDrag"
      ref="dialog"
      :class="[
        'dialog',
        {
          'is-fullscreen': isFullscreen,
        },
      ]"
      @close="onDialogClose"
      @mousedown="onDialogClick"
    >
      <div class="pull-handle" v-drag="handleDrag" @click="onDialogClose" />
      <article class="dialog-content">
        <span class="close-button" v-if="!blockClose">
          <MPButton
            icon="ri-close-line"
            label="close"
            hideLabel
            :iconColor="closeButtonColor"
            size="large"
            aria-label="Close dialog"
            link
            @click="onDialogClose"
          />
        </span>
        <component
          :is="currentComp"
          v-bind="{ ...currentCompProps }"
          ref="dialogComponentRef"
        />
      </article>
    </dialog>
  </Transition>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/scrollbar.pcss";

.dialog-animation-enter-active,
.dialog-animation-leave-active {
  transition: all 0.3s cubic-bezier(0.19, 1, 0.22, 1);
}

.dialog-animation-enter-from,
.dialog-animation-leave-to {
  transform: translateY(100vh);
  opacity: 0;
  @mixin screen md {
    transform: translateY(10vh);
  }
}

dialog {
  opacity: 1;
  position: fixed;
  bottom: 0;
  padding: 0;
  margin: 0 auto;
  width: 100vw;
  max-width: 26rem;
  height: fit-content;
  max-height: 95dvh;
  background-color: transparent;
  overflow: visible;
  margin-top: auto;
  border: 0 !important;
  overflow: hidden;
  overflow-y: scroll;
  @mixin scrollbar-reset;

  @mixin screen md {
    box-shadow: var(--shadow-lg);
    max-width: 100%;
    width: fit-content;
    min-height: auto;
    margin: auto;
    border-radius: 1rem;
  }

  &:focus {
    outline: none;
  }

  &::backdrop {
    background-color: rgba(0, 0, 0, 0.3);
    transition: background-color 5s ease-in;
    backdrop-filter: blur(1px);
  }
}

.dialog.is-fullscreen {
  transform: translateY(0);
  margin: 0;
  top: 0;
  left: 0;
  width: 100vw;
  min-height: 100vh;
  transform: none;
  border-radius: 0;
}

.dialog-content {
  position: relative;
  width: 100%;
  height: fit-content;
  box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.2);
  background-color: var(--main-background-color);
  border-radius: 1rem 1rem 0 0;
  overflow-y: scroll;
  @mixin scrollbar-reset;
  @mixin screen md {
    border-radius: 1rem;
  }
}

.close-button {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 100;
}

.pull-handle {
  display: block;
  width: 100%;
  position: absolute;
  top: -1.75rem;
  padding: 1rem 5rem;
  z-index: 2;
  @mixin screen md {
    display: none;
  }
  &:after {
    position: fixed;
    content: "";
    background-color: white;
    box-shadow: 0 0 4px 0px rgba(0, 0, 0, 0.1);
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2rem;
    width: 5rem;
    height: 0.25rem;
  }
}
</style>
